import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { getPanel } from '../panels/registry.js';

export function usePanels(sharedToken, config) {
  const [isPanelOpen, setIsPanelOpen] = useState(() => {
    if (sharedToken) return false;
    try {
      return sessionStorage.getItem('mta_panel_open') === 'true';
    } catch (e) { }
    return false;
  });

  const [activePanel, setActivePanel] = useState(() => {
    if (sharedToken) return null;
    try {
      const savedMsgId = sessionStorage.getItem('mta_active_panel_message_id');
      const savedType = sessionStorage.getItem('mta_active_panel_type');
      if (savedMsgId && savedType) {
        const savedHistory = localStorage.getItem('mta_chat_history');
        if (savedHistory) {
          const parsed = JSON.parse(savedHistory);
          const found = parsed.find((msg) => msg.id === savedMsgId);
          if (found) {
            const panels = found.panels || [];
            if (found.map && !panels.some((p) => p.type === 'map')) {
              panels.push({ type: 'map', data: found.map, id: `${found.id}_map` });
            }
            if (savedType === 'tips' && found.tips) {
              // Tips state lives on the message itself; ChatBody keeps the panel in sync with it.
              return { type: 'tips', data: found.tips, messageId: savedMsgId };
            }
            const panel = panels.find((p) => p.type === savedType);
            if (panel) {
              return { type: panel.type, data: panel.data, messageId: savedMsgId };
            }
          }
        }
      }
    } catch (e) { }
    return null;
  });

  const [overlayMapData, setOverlayMapData] = useState(null);

  const isPanelOpenRef = useRef(isPanelOpen);
  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen;
  }, [isPanelOpen]);

  const activePanelRef = useRef(activePanel);
  useEffect(() => {
    activePanelRef.current = activePanel;
  }, [activePanel]);

  const panelInstanceRef = useRef(null);

  const openPanel = useCallback((panelAttachment, messageId) => {
    const plugin = getPanel(panelAttachment.type);
    if (!plugin) return;
    if (plugin.isEnabled && !plugin.isEnabled(config)) return;
    setActivePanel({ type: panelAttachment.type, data: panelAttachment.data, messageId });
    setIsPanelOpen(true);
    try {
      sessionStorage.setItem('mta_panel_open', 'true');
      sessionStorage.setItem('mta_active_panel_message_id', messageId);
      sessionStorage.setItem('mta_active_panel_type', panelAttachment.type);
    } catch (e) { }
  }, [config]);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    if (panelInstanceRef.current) {
      panelInstanceRef.current.destroy();
      panelInstanceRef.current = null;
    }
    setActivePanel(null);
    setOverlayMapData(null);
    try {
      sessionStorage.setItem('mta_panel_open', 'false');
      sessionStorage.removeItem('mta_active_panel_message_id');
      sessionStorage.removeItem('mta_active_panel_type');
    } catch (e) { }
  }, []);

  return {
    isPanelOpen,
    setIsPanelOpen,
    activePanel,
    setActivePanel,
    isPanelOpenRef,
    activePanelRef,
    openPanel,
    closePanel,
    panelInstanceRef,
    overlayMapData,
    setOverlayMapData
  };
}
